import { PopoverClasses } from "@mui/material";
import { NestedCheckboxGroup } from "components/Form/components/CheckboxGroup/CheckboxGroupFormComponent";
import { Popover, popoverPosition } from "components/Popover/Popover";
import {
    CheckboxTriggerButton,
    FilterChipContainer,
} from "containers/AdvancedSearch/components/CheckboxTriggerButton";
import { getCheckedRecordValue } from "containers/AdvancedSearch/utils";
import { getLocationNameArray, getLocationOptionsList } from "containers/ExecutionFilter/utils";
import { FormApi } from "final-form";
import { Bundle, Project } from "graphql/generated/graphql";
import { useCallback, useMemo } from "react";
import { Field } from "react-final-form";

interface LocationFilterProps {
    projectList: Project[];
    bundleList: Bundle[];
    currentProjectValue: string[];
    currentBundleValue: string[];
    currentWorkspaceValue?: boolean;
    form: FormApi;
    onProjectChange: (newValues: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBundleChange: (newValues: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    popoverClasses?: Partial<PopoverClasses>;
}

export const LocationFilter = ({
    projectList,
    bundleList,
    currentProjectValue,
    currentBundleValue,
    // currentWorkspaceValue,
    form,
    onProjectChange,
    onBundleChange,
    popoverClasses,
}: LocationFilterProps) => {
    const projectArray = projectList.map((project) => project.id);
    const projectCheckboxState = getCheckedRecordValue(
        projectArray,
        projectArray.length,
        currentProjectValue
    );

    const bundleArray = bundleList.map((project) => project.id);
    const bundleCheckboxState = getCheckedRecordValue(
        bundleArray,
        bundleArray.length,
        currentBundleValue
    );

    const changeAllProjects = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (projectCheckboxState) {
                form.change("location.projects", []);
            } else {
                form.change("location.projects", projectArray);
            }
        },
        [form, projectArray, projectCheckboxState]
    );

    const changeAllBundles = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (bundleCheckboxState) {
                form.change("location.bundles", []);
            } else {
                form.change("location.bundles", bundleArray);
            }
        },
        [bundleArray, bundleCheckboxState, form]
    );

    const currentLocationValue = [...currentProjectValue, ...currentBundleValue];

    const locationText = useMemo(() => {
        if (currentLocationValue.length === projectList?.length + bundleList?.length) {
            return "all";
        }
        if (!currentProjectValue.length && !currentBundleValue.length) {
            return "none";
        }
        let currentFilter: string[] = [];

        const allProjects =
            currentProjectValue?.length > 0 && currentProjectValue?.length === projectList?.length;
        const allBundles =
            currentBundleValue?.length > 0 && currentBundleValue?.length === bundleList?.length;
        if (allProjects && !allBundles) {
            currentFilter = ["Projects", ...getLocationNameArray(currentBundleValue, bundleList)];
        } else if (allBundles && !allProjects) {
            currentFilter = ["Bundles", ...getLocationNameArray(currentProjectValue, projectList)];
        } else {
            currentFilter = [
                ...getLocationNameArray(currentProjectValue, projectList),
                ...getLocationNameArray(currentBundleValue, bundleList),
            ];
        }

        return <FilterChipContainer currentFilter={currentFilter} />;
    }, [
        bundleList,
        currentBundleValue,
        currentLocationValue.length,
        currentProjectValue,
        projectList,
    ]);

    return (
        <Popover
            trigger={
                <CheckboxTriggerButton
                    label="Location"
                    currentFilter={currentLocationValue}
                    totalItemCount={currentLocationValue.length}
                    customFilterText={locationText}
                />
            }
            closeOnInsideClick={false}
            closeOnEnter={true}
            contentClassName="checkbox-group-filter"
            classes={popoverClasses}
            {...popoverPosition}
        >
            <Field name="location.projects" id="location.projects">
                {({ input }) => (
                    <NestedCheckboxGroup
                        mainLabel="Projects"
                        mainCheckboxValue={projectCheckboxState}
                        onChangeAll={changeAllProjects}
                        identifier="location.projects"
                        input={{ ...input, value: currentProjectValue }}
                        options={getLocationOptionsList(projectList)}
                        onChange={onProjectChange}
                    />
                )}
            </Field>
            <Field name="location.bundles" id="location.bundles">
                {({ input }) => (
                    <NestedCheckboxGroup
                        mainLabel="Bundles"
                        mainCheckboxValue={bundleCheckboxState}
                        onChangeAll={changeAllBundles}
                        identifier="location.bundles"
                        input={{ ...input, value: currentBundleValue }}
                        options={getLocationOptionsList(bundleList)}
                        onChange={onBundleChange}
                    />
                )}
            </Field>

            {/* <Field
                    name="location.workspace"
                    id="location.workspace"
                    component={CheckboxGroupFormComponent}
                    fieldProps={{
                        options: [{ value: "workspace", label: "Workspace" }],
                        currentValue: currentWorkspaceValue,
                        onChange: (newValue) => (e) => {
                            onCheckboxValueChange({
                                form,
                                identifier: "location.workspace",
                                currentValue: currentWorkspaceValue,
                                newValue,
                            })(e);
                        },
                    }}
                /> */}
        </Popover>
    );
};
